import { Plus, Trash2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { getDataSource, patchProjectKpis } from "../../lib/datasource";
import { getRoomsTotal, isRoomMixValid } from "../../lib/rooms";
import { RoomType } from "../../types/deal";
import { RoomMixSchema } from "./schemas/roomMix";

type Props = {
  dealId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const MAX_ROOM_TYPES = 5;

export default function RoomMixModal({
  dealId,
  isOpen,
  onClose,
  onSaved,
}: Props) {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    async function loadData() {
      setLoading(true);
      try {
        const ds = getDataSource();
        const project = await ds.getProject(dealId);
        const kpis = project?.kpis || {};

        const mixList = Array.isArray(kpis.roomMix) ? kpis.roomMix : [];

        let seed: RoomType[] = [];

        if (mixList.length > 0) {
          seed = mixList.map((r: any) => ({
            id: r.id || crypto.randomUUID(),
            name: r.name || r.type || "",
            rooms: Number(r.rooms) || Number(r.count) || 0,
            adrWeight: Number(r.adrWeight) || 100,
          }));
        } else {
        }

        setErrors({});
        setRoomTypes(seed);
      } catch (err) {
        console.error("Failed to load room mix:", err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [dealId, isOpen]);

  useEffect(() => {
    try {
      const parsed = RoomMixSchema.parse(roomTypes);
      const errs: Record<string, string> = {};
      const { ok } = isRoomMixValid(parsed);
      setIsValid(ok);
      setErrors(errs);
    } catch (e: any) {
      const errs: Record<string, string> = {};
      if (Array.isArray(e?.issues)) {
        for (const issue of e.issues) {
          const [idx, field] = issue.path as [number, string];
          errs[`${String(field)}-${idx}`] = issue.message;
        }
      }
      setErrors(errs);
      setIsValid(false);
    }
  }, [roomTypes]);

  const handleAddRoomType = () => {
    if (roomTypes.length >= MAX_ROOM_TYPES) return;

    setRoomTypes([
      ...roomTypes,
      {
        id: crypto.randomUUID(),
        name: "",
        rooms: 0,
        adrWeight: 100,
      },
    ]);
  };

  const handleRemoveRoomType = (index: number) => {
    if (roomTypes.length <= 1) return;
    setRoomTypes(roomTypes.filter((_, i) => i !== index));
  };

  const handleRoomTypeChange = (
    index: number,
    field: keyof RoomType,
    value: string | number,
  ) => {
    setRoomTypes((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      if (field === "rooms" || field === "adrWeight") {
        row[field] = Number(value);
      } else if (field === "name") {
        row.name = String(value);
      }
      next[index] = row;
      return next;
    });
  };

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);

    try {
      const parsed = RoomMixSchema.parse(roomTypes);
      const filtered = parsed.filter((rt) => rt.name.trim() !== "");

      const totalRoomsCount = filtered.reduce(
        (sum, rt) => sum + (rt.rooms || 0),
        0,
      );

      const roomMixArray = filtered.map((rt) => ({
        id: rt.id,
        name: rt.name,
        rooms: rt.rooms,
        adrWeight: rt.adrWeight,
      }));

      await patchProjectKpis(dealId, {
        roomMix: roomMixArray,
        rooms: {
          total: totalRoomsCount,
        },
      });

      console.log("[RoomMix] Saved successfully:", roomMixArray);

      onSaved?.();
      onClose();
    } catch (e) {
      console.error("[RoomMix] Failed to save:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const showEmptyState = roomTypes.length === 0;
  const totalRoomsCount = getRoomsTotal(roomTypes);
  const canAddMore = roomTypes.length < MAX_ROOM_TYPES;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col focus:outline-none">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-xl">Edit Room Mix</DialogTitle>
          <p className="text-sm text-slate-500">
            Configure your hotel's room types and total inventory. Maximum{" "}
            {MAX_ROOM_TYPES} room types allowed.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4 relative min-h-[200px]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : showEmptyState ? (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">
                No room types configured yet.
              </p>
              <Button
                onClick={handleAddRoomType}
                disabled={!canAddMore}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Room Type
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Desktop Header */}
              <div className="hidden md:grid md:grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-slate-400 pb-2 border-b border-slate-100">
                <div className="col-span-7">Room Type Name</div>
                <div className="col-span-4 text-right pr-4">Rooms Count</div>
                <div className="col-span-1"></div>
              </div>

              {/* Rows */}
              {roomTypes.map((roomType, index) => (
                <div key={roomType.id} className="group">
                  {/* Mobile Row */}
                  <div className="md:hidden space-y-3 p-4 border border-slate-200 rounded-lg bg-white shadow-sm">
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Name
                      </label>
                      <input
                        type="text"
                        value={roomType.name}
                        onChange={(e) =>
                          handleRoomTypeChange(index, "name", e.target.value)
                        }
                        className={`w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          errors[`name-${index}`]
                            ? "border-red-500"
                            : "border-slate-300"
                        }`}
                        placeholder="e.g. Standard"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">
                        Rooms
                      </label>
                      <input
                        type="number"
                        value={roomType.rooms || ""}
                        onChange={(e) =>
                          handleRoomTypeChange(index, "rooms", e.target.value)
                        }
                        className={`w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          errors[`rooms-${index}`]
                            ? "border-red-500"
                            : "border-slate-300"
                        }`}
                        placeholder="0"
                      />
                    </div>
                    {roomTypes.length > 1 && (
                      <Button
                        variant="ghost"
                        onClick={() => handleRemoveRoomType(index)}
                        className="w-full text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>

                  {/* Desktop Row */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                    <div className="col-span-7">
                      <input
                        type="text"
                        value={roomType.name}
                        onChange={(e) =>
                          handleRoomTypeChange(index, "name", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded-md transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          errors[`name-${index}`]
                            ? "border-red-500"
                            : "border-slate-200 group-hover:border-slate-300"
                        }`}
                        placeholder="e.g. Standard"
                      />
                    </div>
                    <div className="col-span-4">
                      <input
                        type="number"
                        value={roomType.rooms || ""}
                        onChange={(e) =>
                          handleRoomTypeChange(index, "rooms", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded-md text-right transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          errors[`rooms-${index}`]
                            ? "border-red-500"
                            : "border-slate-200 group-hover:border-slate-300"
                        }`}
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {roomTypes.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveRoomType(index)}
                          className="text-slate-300 hover:text-red-500 hover:bg-red-50 h-9 w-9"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <Button
                variant="outline"
                onClick={handleAddRoomType}
                disabled={!canAddMore}
                className="w-full md:w-auto border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Room Type {!canAddMore && `(Max ${MAX_ROOM_TYPES})`}
              </Button>

              <div className="mt-8 p-4 bg-slate-50 rounded-lg flex items-center justify-between border border-slate-100">
                <span className="text-sm font-medium text-slate-600">
                  Total Project Inventory
                </span>
                <span className="text-xl font-bold text-slate-900">
                  {totalRoomsCount} Rooms
                </span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 bg-white border-t border-slate-100">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
            className="border-slate-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || saving || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
